import { Component, Input, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';

@Component({
    selector: 'ashcorp-input',
    templateUrl: './input.component.html',
    styleUrls: ['./input.component.css'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class AshcorpInputComponent {
    @Input() form!: FormGroup;
    @Input() label = '';
    @Input() name = '';
    @Input() type = 'text';
    @Input() required = false;
    @Input() readonly = false;
    @Input() pattern_message = '';

    get id(): string {
        return `input-${this.name || this.label || 'field'}`.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    }

    get isDateType(): boolean {
        return `${this.type || ''}`.trim().toLowerCase() === 'date';
    }

    get datePickerValue(): Date | null {
        const value = this.control?.value;

        if (!value) {
            return null;
        }

        if (value instanceof Date) {
            return isNaN(value.getTime()) ? null : value;
        }

        if (typeof value === 'string') {
            const parsed = new Date(value);
            return isNaN(parsed.getTime()) ? null : parsed;
        }

        return null;
    }

    get control(): AbstractControl | null {
        if (!this.form || !this.name) {
            return null;
        }

        return this.form.get(this.name);
    }

    get errorMessage(): string {
        const control = this.control;
        if (!control) {
            return '';
        }

        const serverError = control.getError('server');
        if (serverError) {
            return serverError;
        }

        if (control.hasError('required')) {
            return `${this.label || this.name} is required.`;
        }

        if (control.hasError('email')) {
            return `Please enter a valid ${this.label || this.name}.`;
        }

        if (control.hasError('pattern')) {
            return this.pattern_message || `Please enter a valid ${this.label || this.name}.`;
        }

        const minlength = control.getError('minlength');
        if (minlength?.requiredLength) {
            return `${this.label || this.name} must be at least ${minlength.requiredLength} characters.`;
        }

        return '';
    }

    onDateChange(event: MatDatepickerInputEvent<Date>): void {
        const control = this.control;
        if (!control) {
            return;
        }

        if (!event.value) {
            control.setValue('');
            control.markAsDirty();
            control.markAsTouched();
            return;
        }

        const year = event.value.getFullYear();
        const month = `${event.value.getMonth() + 1}`.padStart(2, '0');
        const day = `${event.value.getDate()}`.padStart(2, '0');

        control.setValue(`${year}-${month}-${day}`);
        control.markAsDirty();
        control.markAsTouched();
    }
}
